/**
 * P7-5 — the office Re-send dialog (plan § 20).
 *
 * A parcel never arrived. The order is FULFILLED in Shopify, so it is invisible to the entire production
 * board (§ 14.1), and this is the only surface that can re-make it. Reads `GET .../resend-preview`
 * (P7-3, zero writes) and confirms through `POST .../resend` (P7-4).
 *
 * ── THE MONEY PROPERTIES THIS COMPONENT OWNS ───────────────────────────────────────────────────────
 *
 * 1. ⛔ **§ 19.6's requirement.** A line with reprint work already outstanding is NOT pre-selected — it
 *    needs a deliberate tick, and the tick EXPIRES if what it agreed to moves. Two concurrent re-sends
 *    are the one double-spend P7-4 does not refuse server-side, so this dialog is the guard.
 * 2. **ONE token for the dialog's whole life** (§ 19.1). Minted on open, never per attempt — that is what
 *    makes a failed confirm safely retryable instead of a double-spend.
 * 3. **The result leads with `queuedGarments`, never with the 2xx.** P7-4 answers 200 for a fully refused
 *    confirm, and an all-merged line reports `queuedGarments: 0`.
 * 4. **The cost shown is the SELECTION's**, recomputed here — never `preview.totals`, which covers every
 *    eligible line and would name a number the operator did not tick.
 *
 * ⚠️ NOT BUILT, deliberately, each for a stated reason (§ 20.3):
 *   · the `needs_override` attestation — P7-4 refuses those lines unconditionally, so a tick here would
 *     be a lie. It needs an API change first, and that change must ship DASHBOARD-FIRST (§ 5.2).
 *   · art-CONTENT identity ("is today's file the file that shipped") — needs `ListObjectVersions` per
 *     design and is unanswerable for a third of the corpus (§ 17.6). The dialog shows `artUpdatedAt`,
 *     which is an art-VERSION signal and is labelled as one.
 *   · the "today's blank differs from the one consumed" advisory — the blank ledger has NO order-keyed
 *     index (§ 14.2 item 3, verified with a control), so it is not a cheap lookup.
 */

import type {
  ResendConfirmResponse,
  ResendPreviewLine,
  ResendPreviewResponse,
} from '@fsp/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2, PackageX, RefreshCw } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { useApiClient } from '@/lib/api/hooks';
import { cn } from '@/lib/utils';
import {
  basisSentence,
  EMPTY_SELECTION,
  isSelectable,
  isSelected,
  mintResendToken,
  needsReview,
  outcomeLabel,
  preConfirmOtherCohorts,
  resultSummary,
  reviewTokenOf,
  type SelectionState,
  selectedLineIds,
  selectionCost,
} from './resend-lib';

type Props = {
  storeId: string;
  orderId: string;
  orderName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ResendDialog({ storeId, orderId, orderName, open, onOpenChange }: Props) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const [sel, setSel] = useState<SelectionState>(EMPTY_SELECTION);
  const [reason, setReason] = useState<'LOST_IN_TRANSIT' | 'OTHER'>('LOST_IN_TRANSIT');
  const [note, setNote] = useState('');
  const [result, setResult] = useState<ResendConfirmResponse | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  /**
   * ⚠️ ONE TOKEN PER DIALOG SESSION — minted when `open` goes true, and STABLE for every attempt after
   * that. See `mintResendToken`: re-minting per attempt double-spends on a retry, and a constant merges
   * two independent re-sends into one film. Re-minting on OPEN (not on mount) is what makes a second
   * re-send later in the same page visit a genuinely new intent.
   */
  const [token, setToken] = useState<string>('');
  useEffect(() => {
    if (!open) return;
    setToken(mintResendToken());
    setSel(EMPTY_SELECTION);
    setResult(null);
    setProblem(null);
    setReason('LOST_IN_TRANSIT');
    setNote('');
  }, [open]);

  const previewQuery = useQuery({
    queryKey: ['resend-preview', storeId, orderId],
    enabled: open,
    // A spend decision must not be made from a cached page. Refetched on every open.
    staleTime: 0,
    gcTime: 0,
    queryFn: async () => {
      const { data, error } = await client.GET('/orders/{storeId}/{orderId}/resend-preview', {
        params: { path: { storeId, orderId } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the re-send preview.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data as ResendPreviewResponse;
    },
  });
  const preview = previewQuery.data;

  const cost = useMemo(
    () => (preview ? selectionCost(preview, sel) : { lines: 0, garments: 0, films: 0 }),
    [preview, sel],
  );

  /**
   * ⚠️ SYNCHRONOUS double-submit guard, and `confirm.isPending` is NOT a substitute.
   *
   * Found by review probe, not by the 9 mutations: two rapid clicks BOTH fire, because `isPending` is
   * React state and the second click lands before the re-render that disables the button. Measured: 2
   * POSTs.
   *
   * The SPEND was already safe — both carry the same session token, so the second merges server-side.
   * The danger is what the operator is then TOLD: the second response is `already_queued` /
   * "nothing new was queued", and whichever lands last wins the panel. So a double-click could report
   * NOTHING QUEUED for a confirm that queued three shirts — and the natural next move is to reopen the
   * dialog, which mints a NEW token and genuinely double-spends. A misleading report is upstream of a
   * real over-print here, which is why this is fixed rather than noted.
   *
   * A ref, because it is set during the event handler and read before any render can intervene.
   */
  const inFlight = useRef(false);

  const confirm = useMutation({
    mutationFn: async () => {
      if (!preview) throw new Error('No preview loaded.');
      const lineIds = selectedLineIds(preview, sel);
      const { data, error } = await client.POST('/orders/{storeId}/{orderId}/resend', {
        params: { path: { storeId, orderId } },
        body: {
          rebuildFingerprint: preview.rebuildFingerprint,
          resendToken: token,
          lineIds,
          reason,
          ...(reason === 'OTHER' ? { note: note.trim() } : {}),
        },
      });
      if (error) {
        return { kind: 'error' as const, detail: error?.error?.detail, type: error?.error?.type };
      }
      if (!data?.ok) throw new Error('Empty response from the server.');
      return { kind: 'ok' as const, data: data.data as ResendConfirmResponse };
    },
    onSuccess: (res) => {
      if (res.kind === 'error') {
        setProblem(res.detail ?? 'The re-send was refused.');
        // A stale fingerprint is the one refusal with a mechanical fix, so refetch the preview for them
        // rather than making the operator find the Refresh control.
        if (res.type === 'resend_fingerprint_stale') void previewQuery.refetch();
        return;
      }
      setProblem(null);
      setResult(res.data);
      // The queue, the order's own reprint lookup and the board all moved.
      void queryClient.invalidateQueries({ queryKey: ['reprint-queue'] });
      void queryClient.invalidateQueries({ queryKey: ['order-reprints', storeId, orderId] });
      void queryClient.invalidateQueries({ queryKey: ['production-board'] });
    },
    onError: (err) => setProblem(err instanceof Error ? err.message : 'Network error.'),
    // Cleared on BOTH outcomes, so a failed confirm is retryable — which is the whole reason the token
    // is session-stable. `onSettled` rather than a pair of handlers so no branch can forget.
    onSettled: () => {
      inFlight.current = false;
    },
  });

  /** The only path to a spend. Refuses a second concurrent submit — see `inFlight`. */
  const submit = () => {
    if (inFlight.current) return;
    inFlight.current = true;
    confirm.mutate();
  };

  const toggle = (line: ResendPreviewLine) => {
    const others = preConfirmOtherCohorts(line);
    setSel((prev) => {
      if (needsReview(line, others)) {
        const next = new Map(prev.reviewed);
        const tok = reviewTokenOf(line, others);
        if (next.get(line.lineId) === tok) next.delete(line.lineId);
        else next.set(line.lineId, tok);
        return { ...prev, reviewed: next };
      }
      const next = new Set(prev.deselected);
      if (next.has(line.lineId)) next.delete(line.lineId);
      else next.add(line.lineId);
      return { ...prev, deselected: next };
    });
  };

  const noteMissing = reason === 'OTHER' && note.trim().length === 0;
  const canConfirm = cost.garments > 0 && !noteMissing && !confirm.isPending && token.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl">
        <DialogHeader>
          <DialogTitle>Re-send {orderName}</DialogTitle>
          <DialogDescription>
            This order already shipped. Re-sending re-makes the garments the customer is still owed
            — each one takes a fresh blank and new film. Nothing is charged to the customer.
          </DialogDescription>
        </DialogHeader>

        {previewQuery.isLoading && (
          <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Working out what a rebuild would print…
          </p>
        )}

        {previewQuery.isError && (
          <Alert variant="destructive">
            <AlertTitle>Couldn’t load the preview</AlertTitle>
            <AlertDescription>
              {previewQuery.error instanceof Error ? previewQuery.error.message : 'Unknown error.'}
            </AlertDescription>
          </Alert>
        )}

        {/* ── THE RESULT, once confirmed ─────────────────────────────────────────────────────────── */}
        {result && <ResultPanel result={result} />}

        {problem && (
          <Alert variant="destructive">
            <AlertTitle>Not re-sent</AlertTitle>
            <AlertDescription>{problem}</AlertDescription>
          </Alert>
        )}

        {preview && !result && (
          <div className="space-y-4">
            {preview.lines.length === 0 && (
              <p className="text-sm text-muted-foreground">This order has no lines to re-send.</p>
            )}

            {preview.lines.map((line) => {
              const others = preConfirmOtherCohorts(line);
              const review = needsReview(line, others);
              const selectable = isSelectable(line);
              const checked = isSelected(line, others, sel);
              const basis = basisSentence(line);
              return (
                <div
                  key={line.lineId}
                  className={cn(
                    'rounded-md border p-3',
                    !selectable && 'bg-muted/40',
                    review && !checked && 'border-amber-500/60',
                  )}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id={`resend-${line.lineId}`}
                      checked={checked}
                      disabled={!selectable}
                      onCheckedChange={() => toggle(line)}
                      aria-label={`Re-send ${line.title}`}
                    />
                    <div className="min-w-0 flex-1 space-y-1">
                      <Label
                        htmlFor={`resend-${line.lineId}`}
                        className="flex flex-wrap items-center gap-2 font-medium"
                      >
                        {line.title}
                        {line.variantTitle && (
                          <span className="text-muted-foreground">· {line.variantTitle}</span>
                        )}
                        {line.status === 'eligible' && line.resendQty !== null && (
                          <Badge variant="outline">
                            {line.resendQty} owed · {line.garments.length * line.resendQty} film
                            {line.garments.length * line.resendQty === 1 ? '' : 's'}
                          </Badge>
                        )}
                        {line.status === 'needs_override' && (
                          <Badge className="border-transparent bg-amber-600 text-white">
                            Needs a manager
                          </Badge>
                        )}
                        {line.status === 'blocked' && (
                          <Badge variant="secondary">Can’t re-send</Badge>
                        )}
                      </Label>

                      {/* § 17.1 — show the arithmetic, not just its answer. */}
                      {basis && <p className="text-xs text-muted-foreground">{basis}</p>}

                      {/* Server-owned refusal copy, VERBATIM. */}
                      {line.refusal && <p className="text-sm text-destructive">{line.refusal}</p>}

                      {/* ⛔ § 19.6 — the gate. Shown BEFORE the operator can tick, and the tick expires
                          if the sibling set moves (see `reviewTokenOf`). */}
                      {review && (
                        <Alert className="mt-2 border-amber-500/60">
                          <AlertTriangle className="size-4" />
                          <AlertTitle className="text-sm">
                            A reprint is already in flight for this garment
                          </AlertTitle>
                          <AlertDescription className="text-xs">
                            Something for this order is already in the reprint queue or on a reprint
                            sheet. Re-sending adds MORE shirts on top of it — check the reprint
                            queue first, then tick this line deliberately if you still want both.
                          </AlertDescription>
                        </Alert>
                      )}

                      {line.garments.length > 0 && (
                        <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                          {line.garments.map((g) => (
                            <li key={`${g.location}-${g.designId}`}>
                              <span className="font-medium">{g.location}</span> ·{' '}
                              {g.designName ?? g.designId} · {g.widthIn.toFixed(2)}×
                              {g.heightIn.toFixed(2)}in · {g.sizeBand}
                              {g.resolvedBlankSku ? ` · ${g.resolvedBlankSku}` : ''}
                              {/* An art-VERSION signal, not "the art changed since it shipped" — see
                                  ResendPreviewGarmentSchema.artUpdatedAt, which is 100% false-positive
                                  for that question. Labelled so nobody reads it as provenance. */}
                              <span title="When this design record was last touched (not proof the art changed)">
                                {' '}
                                · art v{g.artUpdatedAt.slice(0, 10)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* ── Why ────────────────────────────────────────────────────────────────────────────── */}
            <div className="space-y-2 rounded-md border p-3">
              <Label className="text-sm font-medium">Why are you re-sending?</Label>
              <RadioGroup
                value={reason}
                onValueChange={(v) => setReason(v as 'LOST_IN_TRANSIT' | 'OTHER')}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="LOST_IN_TRANSIT" id="reason-lost" />
                  <Label htmlFor="reason-lost" className="font-normal">
                    The carrier lost the parcel
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="OTHER" id="reason-other" />
                  <Label htmlFor="reason-other" className="font-normal">
                    Something else (say what)
                  </Label>
                </div>
              </RadioGroup>
              {reason === 'OTHER' && (
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. Customer says the box arrived empty."
                  aria-label="Why are you re-sending?"
                  maxLength={500}
                />
              )}
              {/* The shop's own reasons are NOT offered here: PRESS_DEFECT / CUT_DAMAGE describe a
                  garment ruined in production and are reported at the station by whoever ruined it.
                  `/activity`'s reprint lens reads `reason` to tell the carrier's fault from ours. */}
            </div>
          </div>
        )}

        <DialogFooter className="flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between">
          {/* `role="status"` + `aria-live` so the cost is ANNOUNCED as the selection changes rather than
              silently re-rendering — this is the number the operator is agreeing to spend. It also makes
              the cost independently queryable in tests, which matters because the button label repeats
              the shirt count and a bare text match cannot tell the two apart. */}
          <p className="text-sm" role="status" aria-live="polite">
            {result ? (
              <Button variant="outline" size="sm" onClick={() => void previewQuery.refetch()}>
                <RefreshCw className="size-4" /> Re-check this order
              </Button>
            ) : (
              <span className={cn(cost.garments === 0 && 'text-muted-foreground')}>
                <span className="font-medium">
                  {cost.garments} shirt{cost.garments === 1 ? '' : 's'}
                </span>{' '}
                · {cost.films} film{cost.films === 1 ? '' : 's'} · {cost.garments} fresh blank
                {cost.garments === 1 ? '' : 's'}
              </span>
            )}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {result ? 'Done' : 'Cancel'}
            </Button>
            {!result && (
              <Button
                onClick={submit}
                disabled={!canConfirm}
                title={
                  noteMissing
                    ? 'Say what happened first.'
                    : cost.garments === 0
                      ? 'Tick at least one line.'
                      : undefined
                }
              >
                {confirm.isPending && <Loader2 className="size-4 animate-spin" />}
                Queue {cost.garments} shirt{cost.garments === 1 ? '' : 's'} to reprint
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * ⚠️ LEADS WITH THE SPEND, not the HTTP status. `POST .../resend` answers 200 for a completely refused
 * confirm and for an all-merged one, so "it worked" is not a thing this panel may ever say on its own.
 */
function ResultPanel({ result }: { result: ResendConfirmResponse }) {
  const { tone, headline } = resultSummary(result);
  return (
    <div className="space-y-3">
      <Alert variant={tone === 'ok' ? 'default' : 'destructive'}>
        {tone === 'ok' ? <PackageX className="size-4" /> : <AlertTriangle className="size-4" />}
        <AlertTitle>{headline}</AlertTitle>
        {tone === 'warn' && (
          <AlertDescription className="text-xs">
            Re-opening this dialog starts a new re-send, so you can still queue more if the customer
            is genuinely owed another set.
          </AlertDescription>
        )}
      </Alert>
      <ul className="space-y-1 text-sm">
        {result.lines.map((o) => {
          const label = outcomeLabel(o);
          return (
            <li key={o.lineId} className="flex flex-wrap items-baseline gap-2">
              <Badge
                variant={label.tone === 'ok' ? 'outline' : 'secondary'}
                className={cn(label.tone === 'bad' && 'text-destructive')}
              >
                {o.lineId}
              </Badge>
              <span className={cn(label.tone === 'bad' && 'text-destructive')}>{label.text}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
